// Credit system utilities for EzArt billing
import { getModelConfig, type CreditCost, type SubscriptionTier } from './model-config';

// Special credit costs for non-image operations
export const SPECIAL_CREDIT_COSTS = {
    geminiFlashText: 1,     // Prompt enhancement, passport text, voice analysis
    qwenTTS: 3,             // Per ~1K characters of TTS
    passportGeneration: 2,  // 1 (text) + 1 (Sunburst reference sheet)
    wardrobeSheet: 1,       // ChatGPT Sunburst reference sheet
} as const;

/**
 * Get the credit cost for a model generation, optionally at a specific resolution.
 * Returns the flat cost or resolution-specific cost from the model config.
 */
export function getCreditCost(modelId: string, resolution?: string): number {
    const config = getModelConfig(modelId);
    if (!config) {
        console.warn(`Unknown model for credit cost: ${modelId}, defaulting to 1`);
        return 1;
    }

    const cost = config.creditCost;

    if (typeof cost === 'number') {
        return cost;
    }

    // Resolution-dependent cost
    if (resolution && resolution in cost) {
        return cost[resolution];
    }

    return cost.default;
}

/**
 * Format credit cost for display.
 * e.g., 1 → "1 credit", 15 → "15 credits"
 */
export function formatCreditCost(cost: number): string {
    return `${cost} credit${cost !== 1 ? 's' : ''}`;
}

/**
 * Check if a user's tier meets the minimum requirement for a model.
 */
export function canAccessModel(userTier: SubscriptionTier, modelId: string): boolean {
    const config = getModelConfig(modelId);
    if (!config || !config.minimumTier) return true;

    const tierOrder: SubscriptionTier[] = ['free', 'starter', 'pro'];
    const userTierIndex = tierOrder.indexOf(userTier);
    const requiredTierIndex = tierOrder.indexOf(config.minimumTier);

    return userTierIndex >= requiredTierIndex;
}
